import {type Operation} from '../graphql/apolloLinkRuntime';
import {useServiceHealthStore} from './store';

/**
 * Sliding-window tracker for backend load-shedding (HTTP 503 on /graphql).
 *
 * Every GraphQL query outcome is recorded as a sample; when more than
 * SHOW_THRESHOLD of the samples in the last WINDOW_MS are 503s (and there are
 * enough samples to mean anything), the serviceHealth store flips to
 * degraded, showing the "Service degraded" pill. It flips back once the rate
 * recovers below HIDE_THRESHOLD or the window drains — the show/hide gap is
 * hysteresis so a rate hovering at the threshold doesn't flicker the pill.
 *
 * Sample sources (wired in util/apollo.ts):
 * - 'unavailable': one per 503 response the server actually returned,
 *   recorded per attempt inside retryLink — so an open breaker keeps the rate
 *   pinned while retries are in flight.
 * - 'ok' / 'error': terminal outcomes recorded by serviceHealthLink, which
 *   sits above retryLink. Terminal 503s are skipped there (already counted).
 */

export type SampleKind = 'ok' | 'error' | 'unavailable';

export const WINDOW_MS = 60_000;
export const MIN_SAMPLES = 10;
export const SHOW_THRESHOLD = 0.1;
export const HIDE_THRESHOLD = 0.05;

type Sample = {t: number; unavailable: boolean};

// Module-scope, not store state: recording a sample must not re-render
// anything; only the show/hide verdict goes through the store.
const samples: Sample[] = [];

/** Mutations/subscriptions don't feed the window — it's about reads. */
export function isQueryOperation(operation: Operation): boolean {
  return operation.query.definitions.some(
    def => def.kind === 'OperationDefinition' && def.operation === 'query'
  );
}

export function recordGqlSample(kind: SampleKind): void {
  samples.push({t: Date.now(), unavailable: kind === 'unavailable'});
  evaluateWindow();
}

export function getWindowStats(): {
  total: number;
  unavailable: number;
  rate: number;
} {
  const total = samples.length;
  const unavailable = samples.reduce((n, s) => n + (s.unavailable ? 1 : 0), 0);
  return {total, unavailable, rate: total > 0 ? unavailable / total : 0};
}

/**
 * Prunes expired samples and pushes the show/hide verdict into the store.
 * Called on every sample, and polled by the pill while visible so the window
 * drains (and the pill hides) even when no more queries are being issued.
 */
export function evaluateWindow(): void {
  const cutoff = Date.now() - WINDOW_MS;
  while (samples.length > 0 && samples[0].t < cutoff) {
    samples.shift();
  }
  const {total, rate} = getWindowStats();

  const {degraded, setDegraded} = useServiceHealthStore.getState();
  if (!degraded && total >= MIN_SAMPLES && rate > SHOW_THRESHOLD) {
    setDegraded(true);
  } else if (degraded && (total === 0 || rate < HIDE_THRESHOLD)) {
    setDegraded(false);
  }
}

export function resetForTesting(): void {
  samples.length = 0;
  useServiceHealthStore.setState({degraded: false});
}
